import { FC, useEffect, useRef, useState } from 'react';
import { InputGroup } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { BUTTON_VARIANTS } from '../../constants/button-variants';
import translation from '../../languages';
import { getFilters } from '../../reducers/filters';
import useNewInteractive from '../../routes/new-interactive/hooks/use-new-interactive';
import cn from '../../utils/classnames';
import hidePopover from '../../utils/hide-popover';
import isAuthorDomain from '../../utils/is-author-domain';
import serializeQueryParams from '../../utils/url/serialize-query-params';
import Button from '../button/button';
import FontAwesomeIcon from '../font-awesome-icon/font-awesome-icon';
import Popover from '../popover/popover';
import AutoSuggestInput from './auto-suggest-input/auto-suggest-input';
import SearchDropdown from './search-dropdown/search-dropdown';
import { EventConfigType, SearchConfig, applySearch } from './search.helpers';
import './search.scss';

type SearchPropsType = RouterWithTranslation & {
    stopMobileTransform?: boolean;
    mobileVisibleSearch?: boolean;
};

const Search: FC<SearchPropsType> = (props) => {
    const { history, stopMobileTransform, mobileVisibleSearch, t } = props;
    const baseFilter = {
        id: '',
        title: t('header.searchInput.allSubjects')
    } as SubjectFilter;
    const dispatch = useDispatch();
    const queryParams = serializeQueryParams();
    const author: Author = useSelector((state: RootState) => state.author);
    const filtersState = useSelector((state: RootState) => state.main.filters);
    const [activeFilter, setActiveFilter] = useState<SubjectFilter>(baseFilter);
    const [searchQuery, setSearchQuery] = useState((queryParams.query as string) || '');
    const [filters, setFilters] = useState(filtersState);
    let tempActiveFilter: SubjectFilter = {} as SubjectFilter;
    const isCopyPasted = useRef(false);
    const isNewInteractive = useNewInteractive();
    const shouldShowSearchDropdown = Boolean(isNewInteractive);
    const [shouldSearchOnlyInteractives, setShouldSearchOnlyInteractives] = useState<boolean>(shouldShowSearchDropdown);

    useEffect(() => {
        if (!isNewInteractive) {
            setShouldSearchOnlyInteractives(false);
        }
    }, [isNewInteractive]);

    useEffect(() => {
        setFilters(filtersState);
    }, [filtersState?.subject?.length]);

    useEffect(() => {
        if (author.id && isAuthorDomain() && filtersState) {
            tempActiveFilter = { id: author.id, title: author.details.publicName } as SubjectFilter;
            filters.subject = [{ ...tempActiveFilter }, ...(filtersState?.subject || [])];
        }

        if (!author.id) {
            tempActiveFilter = baseFilter;
            filters.subject = [...(filtersState?.subject || [])];
        }

        setActiveFilter(tempActiveFilter);
    }, [author.id]);

    useEffect(() => {
        dispatch(getFilters());
    }, []);

    const search = () => {
        const isAuthorPage = isAuthorDomain() as boolean;
        const eventConfig: EventConfigType = {
            query: searchQuery,
            isCopyPasted: isCopyPasted?.current
        };
        const config: SearchConfig = {
            author,
            isAuthorPage,
            isInteractive: shouldSearchOnlyInteractives
        };

        applySearch({
            history,
            config,
            eventConfig,
            data: {
                query: searchQuery,
                t: !isAuthorPage && activeFilter.id
            }
        });
    };

    const queryChange = (query: string) => {
        setSearchQuery(query);
    };

    return (
        <div
            className={cn('notranslate main-search-container d-flex align-items-center', {
                interactive: isNewInteractive,
                'search-with-dropdown': shouldShowSearchDropdown
            })}
        >
            <div className='black-focus' />
            <InputGroup
                className={cn('main-search', {
                    'd-flex': stopMobileTransform,
                    'd-lg-flex': !stopMobileTransform
                })}
            >
                {shouldShowSearchDropdown && (
                    <InputGroup.Prepend>
                        <SearchDropdown toggleOnlyInteractivesSearch={setShouldSearchOnlyInteractives} />
                    </InputGroup.Prepend>
                )}
                <AutoSuggestInput
                    activeFilter={activeFilter}
                    author={author}
                    authorsBottom
                    isCopyPasted={isCopyPasted}
                    disableMaterialsSuggestion
                    disableGeneralSuggestion
                    mobileVisibleSearch={mobileVisibleSearch}
                    queryChange={queryChange}
                    isInteractive={shouldSearchOnlyInteractives}
                />
                <InputGroup.Append onClick={search}>
                    <Button className='search-block' variant={BUTTON_VARIANTS.GREEN}>
                        <span>
                            <FontAwesomeIcon name='search' theme='white' />
                        </span>
                    </Button>
                </InputGroup.Append>
            </InputGroup>
            {!stopMobileTransform && (
                <Popover
                    menu={<span />}
                    mobileTitle={
                        <div className='d-flex align-items-center mobile-search-input-wrapper'>
                            <div className='menu-item search-icon-mobile' onClick={hidePopover}>
                                <span>
                                    <FontAwesomeIcon name='search' />
                                </span>
                            </div>
                            <AutoSuggestInput authorsBottom isCopyPasted={isCopyPasted} author={author} />
                        </div>
                    }
                    triggerAs={
                        <div className='d-flex d-lg-none search-icon-mobile'>
                            <span>
                                <FontAwesomeIcon name='search' />
                            </span>
                        </div>
                    }
                />
            )}
        </div>
    );
};

const translate = translation('header');

export default withRouter(translate(Search));
